import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Checkbox,
  useMediaQuery,
  useTheme,
  TablePagination,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Receipt as InvoiceIcon,
  Business as CompanyIcon,
  MoneyOff as UnpaidIcon,
  Description as TimesheetIcon,
  People as PeopleIcon,
  AccountBalance as PayrollIcon,
  ReceiptLong as ExpenseIcon,
  PlayArrow as RunIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  AccountBalanceWallet as AgingIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Timer as TimerIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  DateRange as DateRangeIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  getEmployees,
  getCompanies,
  exportToExcel,
  exportPayroll,
  exportSummary,
  exportOutstandingInvoices,
  exportCompanyHours,
  exportUnpaidBalances,
  exportExpensesDetailed,
  getMyManagedEmployees,
  fetchPayrollReport,
  fetchDetailedTimesheetReport,
  fetchEmployeeSummaryReport,
  fetchOutstandingInvoicesReport,
  fetchCompanyHoursReport,
  fetchUnpaidBalancesReport,
  fetchOutstandingExpensesReport,
  getCustomReports,
  createCustomReport,
  updateCustomReport,
  deleteCustomReport,
  runCustomReport,
  exportCustomReport,
  getPayPeriodPresets,
  fetchARAgingReport,
  fetchLaborCostReport,
  fetchMissingTimesheetsReport,
  fetchProfitMarginReport,
  fetchBillableReport,
  fetchWeeklyTimesheetReport,
  fetchPayPeriodReport,
  exportARAging,
  exportLaborCost,
  exportMissingTimesheets,
  exportProfitMargin,
  exportBillable,
  exportWeeklyTimesheet,
  exportPayPeriodTimesheets,
} from '../api';
import { buildPresetsFromApi } from '../utils/payPeriodUtils';
import ReportBuilderGrid from './reportBuilder/ReportBuilderGrid';
import { useConfirmDialog } from './ConfirmDialog';

// Column definitions matching the backend (key→label mapping)
const COLUMN_DEFS = {
  time_entries: [
    { key: 'employee_name', label: 'Employee' },
    { key: 'employee_email', label: 'Email' },
    { key: 'entry_date', label: 'Date' },
    { key: 'start_time', label: 'Start Time' },
    { key: 'end_time', label: 'End Time' },
    { key: 'break_minutes', label: 'Break (min)' },
    { key: 'hours_worked', label: 'Hours' },
    { key: 'hourly_rate', label: 'Rate' },
    { key: 'total_pay', label: 'Total Pay' },
    { key: 'company_name', label: 'Client' },
    { key: 'affiliate_name', label: 'Affiliate' },
    { key: 'department_name', label: 'Department' },
    { key: 'is_paid', label: 'Paid Status' },
    { key: 'paid_amount', label: 'Paid Amount' },
    { key: 'approval_status', label: 'Approval Status' },
    { key: 'notes', label: 'Notes' },
  ],
  expenses: [
    { key: 'employee_name', label: 'Employee' },
    { key: 'title', label: 'Title' },
    { key: 'expense_type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'total_amount', label: 'Amount' },
    { key: 'start_date', label: 'Start Date' },
    { key: 'end_date', label: 'End Date' },
    { key: 'is_paid', label: 'Paid' },
    { key: 'notes', label: 'Notes' },
  ],
  invoices: [
    { key: 'invoice_number', label: 'Invoice #' },
    { key: 'company_name', label: 'Client' },
    { key: 'invoice_date', label: 'Date' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'subtotal', label: 'Subtotal' },
    { key: 'tax_amount', label: 'Tax' },
    { key: 'total_amount', label: 'Total' },
    { key: 'status', label: 'Status' },
    { key: 'notes', label: 'Notes' },
  ],
};

// ============================================
// Report Type Definitions
// ============================================
const REPORT_TYPES = [
  {
    id: 'overall_payroll',
    label: 'Overall Payroll',
    description: 'Payroll-ready report with overtime calculations',
    icon: <PayrollIcon />,
    filters: ['dateRange', 'employee', 'markAsPaid', 'overtimeThreshold'],
  },
  {
    id: 'detailed_timesheet',
    label: 'Detailed Timesheets',
    description: 'All time entries with full details',
    icon: <TimesheetIcon />,
    filters: ['dateRange', 'employee'],
  },
  {
    id: 'employee_summary',
    label: 'Employee Summary',
    description: 'Aggregated totals per employee',
    icon: <PeopleIcon />,
    filters: ['dateRange'],
  },
  {
    id: 'outstanding_invoices',
    label: 'Outstanding Invoices',
    description: 'Unpaid and draft invoices',
    icon: <InvoiceIcon />,
    filters: ['company'],
  },
  {
    id: 'outstanding_expenses',
    label: 'Outstanding Expenses',
    description: 'Pending and submitted expenses',
    icon: <ExpenseIcon />,
    filters: [],
  },
  {
    id: 'company_hours',
    label: 'Client Hours',
    description: 'Hours grouped by company and employee',
    icon: <CompanyIcon />,
    filters: ['dateRange', 'company'],
  },
  {
    id: 'unpaid_balances',
    label: 'Unpaid Balances',
    description: 'All entries with outstanding balances',
    icon: <UnpaidIcon />,
    filters: ['dateRange', 'employee'],
  },
  {
    id: 'ar_aging',
    label: 'AR Aging',
    description: 'Invoices by days overdue (Current, 1-30, 31-60, 61-90, 90+)',
    icon: <AgingIcon />,
    filters: ['company'],
  },
  {
    id: 'labor_cost',
    label: 'Labor Cost',
    description: 'Hours and labor cost breakdown by company',
    icon: <CompanyIcon />,
    filters: ['dateRange'],
  },
  {
    id: 'missing_timesheets',
    label: 'Missing Timesheets',
    description: 'Employees with no or low hours for a period',
    icon: <WarningIcon />,
    filters: ['dateRange'],
  },
  {
    id: 'profit_margin',
    label: 'Profit Margin',
    description: 'Revenue vs labor cost per client',
    icon: <TrendingUpIcon />,
    filters: ['dateRange', 'company'],
  },
  {
    id: 'billable',
    label: 'Billable Hours',
    description: 'Billable vs non-billable utilization by employee',
    icon: <TimerIcon />,
    filters: ['dateRange', 'employee'],
  },
  {
    id: 'weekly_timesheet',
    label: 'Weekly Timesheet',
    description: 'Print-ready weekly timesheet by employee',
    icon: <CalendarViewWeekIcon />,
    filters: ['dateRange', 'employee'],
  },
  {
    id: 'pay_period_timesheets',
    label: 'Pay Period Summary',
    description: 'Timesheets grouped by pay period',
    icon: <DateRangeIcon />,
    filters: ['dateRange'],
  },
];

export default function ReportRunner() {
  const { user, isAdmin, isManager, hasPermission } = useAuth();
  // Mark as Paid on Overall Payroll writes payment status (server: Manage invoices or Run payroll)
  const canMarkPaid = hasPermission('can_manage_invoices') || hasPermission('can_run_payroll');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();

  // Report state
  const [selectedReportType, setSelectedReportType] = useState('overall_payroll');
  const [employees, setEmployees] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filters, setFilters] = useState({ start_date: '', end_date: '', employee_id: '', company_id: '' });
  const [payPeriodPresets, setPayPeriodPresets] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [skippedUnpaid, setSkippedUnpaid] = useState(0); // entries Mark as Paid left unpaid (outside direct reports)
  const [overtimeThreshold, setOvertimeThreshold] = useState('40');

  // Report results state
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Custom reports
  const [customReports, setCustomReports] = useState([]);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customEditMode, setCustomEditMode] = useState(false);
  const [customEditReport, setCustomEditReport] = useState(null);
  const [customForm, setCustomForm] = useState({
    name: '', description: '', data_source: 'time_entries',
    columns: [], filters: [], grouping: 'none', sort_column: '', sort_order: 'ASC',
  });

  // General
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Column options per data source
  const COLUMN_OPTIONS = {
    time_entries: [
      { value: 'employee_name', label: 'Employee' },
      { value: 'employee_email', label: 'Email' },
      { value: 'entry_date', label: 'Date' },
      { value: 'start_time', label: 'Start Time' },
      { value: 'end_time', label: 'End Time' },
      { value: 'break_minutes', label: 'Break (min)' },
      { value: 'hours_worked', label: 'Hours' },
      { value: 'hourly_rate', label: 'Rate' },
      { value: 'total_pay', label: 'Total Pay' },
      { value: 'company_name', label: 'Client' },
      { value: 'affiliate_name', label: 'Affiliate' },
      { value: 'department_name', label: 'Department' },
      { value: 'is_paid', label: 'Paid Status' },
      { value: 'paid_amount', label: 'Paid Amount' },
      { value: 'approval_status', label: 'Approval Status' },
      { value: 'notes', label: 'Notes' },
    ],
    expenses: [
      { value: 'employee_name', label: 'Employee' },
      { value: 'title', label: 'Title' },
      { value: 'expense_type', label: 'Type' },
      { value: 'status', label: 'Status' },
      { value: 'total_amount', label: 'Amount' },
      { value: 'start_date', label: 'Start Date' },
      { value: 'end_date', label: 'End Date' },
      { value: 'is_paid', label: 'Paid' },
      { value: 'notes', label: 'Notes' },
    ],
    invoices: [
      { value: 'invoice_number', label: 'Invoice #' },
      { value: 'company_name', label: 'Client' },
      { value: 'invoice_date', label: 'Date' },
      { value: 'due_date', label: 'Due Date' },
      { value: 'subtotal', label: 'Subtotal' },
      { value: 'tax_amount', label: 'Tax' },
      { value: 'total_amount', label: 'Total' },
      { value: 'status', label: 'Status' },
      { value: 'notes', label: 'Notes' },
    ],
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [empRes, compRes, customRes, presetsRes] = await Promise.all([
        isAdmin ? getEmployees() : isManager ? getMyManagedEmployees().catch(() => getEmployees()) : getEmployees(),
        getCompanies(),
        (isAdmin || isManager) ? getCustomReports().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        getPayPeriodPresets().catch(() => ({ data: [] })),
      ]);
      setEmployees(empRes.data || []);
      setCompanies(compRes.data || []);
      setCustomReports(customRes.data || []);
      setPayPeriodPresets(buildPresetsFromApi(presetsRes.data));
    } catch (err) {
      console.error('Failed to load report data:', err);
    }
  };

  // ============================================
  // Report Download
  // ============================================
  const handleReportTypeChange = (id) => {
    setSelectedReportType(id);
    setReportData(null);
    setPage(0);
  };

  // Check if current selection is a custom report
  const isCustomReport = selectedReportType.startsWith('custom_');
  const selectedCustomReport = isCustomReport
    ? customReports.find(r => `custom_${r.id}` === selectedReportType)
    : null;

  const handlePeriodChange = (e) => {
    const value = e.target.value;
    setSelectedPeriod(value);
    const preset = payPeriodPresets.find(p => p.value === value);
    if (preset) {
      const { start, end } = preset.getValue();
      setFilters(prev => ({
        ...prev,
        start_date: start,
        end_date: end,
        include_unpaid_before: preset.includeUnpaidPrevious && start ? start : '',
      }));
    }
  };

  const handleDownloadReport = async () => {
    setSkippedUnpaid(0);
    const params = {};
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.include_unpaid_before) params.include_unpaid_before = filters.include_unpaid_before;

    try {
      // Handle custom reports
      if (isCustomReport && selectedCustomReport) {
        if (filters.employee_id) params.employee_id = filters.employee_id;
        if (filters.company_id) params.company_id = filters.company_id;
        await exportCustomReport(selectedCustomReport.id, params);
        return;
      }

      switch (selectedReportType) {
        case 'overall_payroll':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          if (markAsPaid && canMarkPaid) params.mark_as_paid = 'true';
          params.overtime_threshold = overtimeThreshold;
          const dl = await exportPayroll(params);
          setSkippedUnpaid(markAsPaid && canMarkPaid ? (dl?.entriesSkipped || 0) : 0);
          break;
        case 'detailed_timesheet':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          await exportToExcel(params);
          break;
        case 'employee_summary':
          await exportSummary(params);
          break;
        case 'outstanding_invoices':
          if (filters.company_id) params.company_id = filters.company_id;
          await exportOutstandingInvoices(params);
          break;
        case 'outstanding_expenses':
          await exportExpensesDetailed(params);
          break;
        case 'company_hours':
          if (filters.company_id) params.company_id = filters.company_id;
          await exportCompanyHours(params);
          break;
        case 'unpaid_balances':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          await exportUnpaidBalances(params);
          break;
        case 'ar_aging':
          if (filters.company_id) params.company_id = filters.company_id;
          await exportARAging(params);
          break;
        case 'labor_cost':
          await exportLaborCost(params);
          break;
        case 'missing_timesheets':
          await exportMissingTimesheets(params);
          break;
        case 'profit_margin':
          if (filters.company_id) params.company_id = filters.company_id;
          await exportProfitMargin(params);
          break;
        case 'billable':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          await exportBillable(params);
          break;
        case 'weekly_timesheet':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          await exportWeeklyTimesheet(params);
          break;
        case 'pay_period_timesheets':
          await exportPayPeriodTimesheets(params);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.message || 'Failed to download report');
    }
  };

  // Custom report CRUD handlers

  const handleEditCustomReport = (report) => {
    const config = report.config || {};
    setCustomForm({
      name: report.name, description: report.description || '',
      data_source: report.data_source,
      columns: config.columns || [], filters: config.filters || [],
      grouping: config.grouping || 'none', sort_column: config.sort_column || '', sort_order: config.sort_order || 'ASC',
    });
    setCustomEditMode(true);
    setCustomEditReport(report);
    setCustomDialogOpen(true);
  };

  const handleDeleteCustomReport = async (report) => {
    const confirmed = await showConfirm({
      title: 'Delete Custom Report',
      message: `Delete custom report "${report.name}"?`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteCustomReport(report.id);
      setSuccess('Report deleted');
      loadData();
      if (selectedReportType === `custom_${report.id}`) {
        setSelectedReportType('overall_payroll');
        setReportData(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete report');
    }
  };

  const handleSaveCustomReport = async () => {
    if (!customForm.name) { setError('Report name is required'); return; }
    if (customForm.columns.length === 0) { setError('Select at least one column'); return; }
    try {
      const payload = {
        name: customForm.name, description: customForm.description, data_source: customForm.data_source,
        config: { columns: customForm.columns, filters: customForm.filters, grouping: customForm.grouping, sort_column: customForm.sort_column, sort_order: customForm.sort_order },
      };
      if (customEditMode && customEditReport) {
        await updateCustomReport(customEditReport.id, payload);
        setSuccess('Report updated');
      } else {
        await createCustomReport(payload);
        setSuccess('Report created');
      }
      setCustomDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save report');
    }
  };

  const handleRunReport = async () => {
    try {
      setReportLoading(true);
      setReportData(null);
      setPage(0);
      setError('');

      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.include_unpaid_before) params.include_unpaid_before = filters.include_unpaid_before;

      let res;

      // Handle custom reports
      if (isCustomReport && selectedCustomReport) {
        if (filters.employee_id) params.employee_id = filters.employee_id;
        if (filters.company_id) params.company_id = filters.company_id;
        res = await runCustomReport(selectedCustomReport.id, params);
        setReportData(res.data);
        setReportLoading(false);
        return;
      }

      switch (selectedReportType) {
        case 'overall_payroll':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          params.overtime_threshold = overtimeThreshold;
          // Never pass mark_as_paid for JSON preview
          res = await fetchPayrollReport(params);
          break;
        case 'detailed_timesheet':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          res = await fetchDetailedTimesheetReport(params);
          break;
        case 'employee_summary':
          res = await fetchEmployeeSummaryReport(params);
          break;
        case 'outstanding_invoices':
          if (filters.company_id) params.company_id = filters.company_id;
          res = await fetchOutstandingInvoicesReport(params);
          break;
        case 'outstanding_expenses':
          res = await fetchOutstandingExpensesReport(params);
          break;
        case 'company_hours':
          if (filters.company_id) params.company_id = filters.company_id;
          res = await fetchCompanyHoursReport(params);
          break;
        case 'unpaid_balances':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          res = await fetchUnpaidBalancesReport(params);
          break;
        case 'ar_aging':
          if (filters.company_id) params.company_id = filters.company_id;
          res = await fetchARAgingReport(params);
          break;
        case 'labor_cost':
          res = await fetchLaborCostReport(params);
          break;
        case 'missing_timesheets':
          res = await fetchMissingTimesheetsReport(params);
          break;
        case 'profit_margin':
          if (filters.company_id) params.company_id = filters.company_id;
          res = await fetchProfitMarginReport(params);
          break;
        case 'billable':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          res = await fetchBillableReport(params);
          break;
        case 'weekly_timesheet':
          if (filters.employee_id) params.employee_id = filters.employee_id;
          res = await fetchWeeklyTimesheetReport(params);
          break;
        case 'pay_period_timesheets':
          res = await fetchPayPeriodReport(params);
          break;
        default:
          return;
      }
      setReportData(res.data);
    } catch (err) {
      console.error('Failed to run report:', err);
      setError(err.response?.data?.error || 'Failed to run report');
    } finally {
      setReportLoading(false);
    }
  };

  // Mirrors the server gates in routes/reports.js (requireInvoiceReportAccess /
  // requireProfitMarginAccess). Built-in admin roles carry every flag.
  const canInvoices = hasPermission('can_manage_invoices');
  const reportVisible = {
    outstanding_invoices: canInvoices,
    ar_aging: canInvoices,
    profit_margin: canInvoices && hasPermission('can_view_pay_rates') && hasPermission('can_view_all_employees'),
  };
  const visibleReportTypes = REPORT_TYPES.filter(rt => reportVisible[rt.id] !== false);

  const currentReport = REPORT_TYPES.find(r => r.id === selectedReportType);
  const customConfig = selectedCustomReport?.config || {};
  const customFilters = customConfig.filters || [];
  const showDateRange = isCustomReport ? customFilters.includes('dateRange') : currentReport?.filters.includes('dateRange');
  const showEmployee = isCustomReport ? customFilters.includes('employee') : currentReport?.filters.includes('employee');
  const showCompany = isCustomReport ? customFilters.includes('company') : currentReport?.filters.includes('company');
  const showMarkAsPaid = canMarkPaid && !isCustomReport && currentReport?.filters.includes('markAsPaid');
  const showOvertimeThreshold = !isCustomReport && currentReport?.filters.includes('overtimeThreshold');
  const reportLabel = isCustomReport ? selectedCustomReport?.name : currentReport?.label;
  const reportDescription = isCustomReport ? (selectedCustomReport?.description || `Custom ${selectedCustomReport?.data_source?.replace('_', ' ')} report`) : currentReport?.description;

  return (
    <Box>
      {skippedUnpaid > 0 && (
        <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />} onClose={() => setSkippedUnpaid(0)} sx={{ mb: 2 }}>
          {skippedUnpaid} {skippedUnpaid === 1 ? 'entry' : 'entries'} outside your direct reports {skippedUnpaid === 1 ? 'was' : 'were'} left unpaid
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {/* ===================== SECTION A: Run Reports ===================== */}
      <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          <DownloadIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Run a Report
        </Typography>

        {/* Report Type Cards */}
        <Grid container spacing={isMobile ? 1 : 1.5} sx={{ mb: 3 }}>
          {visibleReportTypes.map((rt) => (
            <Grid item xs={6} sm={4} md={3} lg={12 / 7} key={rt.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderColor: selectedReportType === rt.id ? 'primary.main' : undefined,
                  borderWidth: selectedReportType === rt.id ? 2 : 1,
                  bgcolor: selectedReportType === rt.id ? 'primary.soft' : 'background.paper',
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main' },
                  height: '100%',
                }}
              >
                <CardActionArea onClick={() => handleReportTypeChange(rt.id)} sx={{ height: '100%', p: isMobile ? 1 : 1.5 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ color: selectedReportType === rt.id ? 'primary.dark' : 'text.secondary', mb: 0.5 }}>{rt.icon}</Box>
                    <Typography variant="caption" fontWeight={selectedReportType === rt.id ? 'bold' : 'medium'} sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', lineHeight: 1.2, display: 'block' }}>
                      {rt.label}
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Custom Reports Section */}
        {(isAdmin || isManager) && (
          <>
            {customReports.length > 0 && <Divider sx={{ my: 2 }}><Chip label="Custom Reports" size="small" /></Divider>}
            <Grid container spacing={isMobile ? 1 : 1.5} sx={{ mb: 3 }}>
              {customReports.map((cr) => {
                const crId = `custom_${cr.id}`;
                const isSelected = selectedReportType === crId;
                return (
                  <Grid item xs={6} sm={4} md={12 / 7} key={crId}>
                    <Card
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        borderColor: isSelected ? 'primary.main' : undefined,
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'primary.soft' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main' },
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <CardActionArea onClick={() => handleReportTypeChange(crId)} sx={{ height: '100%', p: isMobile ? 1 : 1.5 }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box sx={{ color: isSelected ? 'primary.dark' : 'text.secondary', mb: 0.5 }}><AssessmentIcon fontSize="small" /></Box>
                          <Typography variant="caption" fontWeight={isSelected ? 'bold' : 'medium'} sx={{ fontSize: isMobile ? '0.65rem' : '0.75rem', lineHeight: 1.2, display: 'block' }}>
                            {cr.name}
                          </Typography>
                        </Box>
                      </CardActionArea>
                      <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex' }}>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditCustomReport(cr); }} sx={{ p: 0.25 }}>
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteCustomReport(cr); }} sx={{ p: 0.25 }}>
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {/* Configuration Panel */}
        <Box sx={{ p: isMobile ? 1.5 : 2, bgcolor: 'background.surface2', border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {reportDescription}
          </Typography>

          <Grid container spacing={2}>
            {/* Date Range */}
            {showDateRange && (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth type="date" label="Start Date"
                    value={filters.start_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth type="date" label="End Date"
                    value={filters.end_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
              </>
            )}

            {/* Employee Dropdown */}
            {showEmployee && (
              <Grid item xs={12} sm={showDateRange ? 6 : 6}>
                <TextField
                  fullWidth select label="Employee (Optional)"
                  value={filters.employee_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, employee_id: e.target.value }))}
                  size="small"
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.id || emp.employee_id} value={emp.id || emp.employee_id}>
                      {emp.first_name} {emp.last_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* Company Dropdown */}
            {showCompany && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth select label="Client (Optional)"
                  value={filters.company_id}
                  onChange={(e) => setFilters(prev => ({ ...prev, company_id: e.target.value }))}
                  size="small"
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {companies.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {/* OT Threshold */}
            {showOvertimeThreshold && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth type="number" label="OT Threshold (hrs/wk)"
                  value={overtimeThreshold}
                  onChange={(e) => setOvertimeThreshold(e.target.value)}
                  size="small"
                  inputProps={{ min: 0, max: 168 }}
                />
              </Grid>
            )}

            {/* Mark as Paid */}
            {showMarkAsPaid && (
              <Grid item xs={6} sm={3}>
                <FormControlLabel
                  control={<Checkbox checked={markAsPaid} onChange={(e) => setMarkAsPaid(e.target.checked)} size="small" />}
                  label={<Typography variant="body2">Mark as Paid</Typography>}
                />
                {user?.role !== 'admin' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                    Marks only entries of your direct reports (never your own).
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>

          {/* Pay Period Quick Select */}
          {showDateRange && (
            <Box sx={{ mt: 1.5 }}>
              <TextField
                select
                label="Pay Period"
                value={selectedPeriod}
                onChange={handlePeriodChange}
                size="small"
                sx={{ minWidth: 200 }}
              >
                {payPeriodPresets.map((preset) => (
                  <MenuItem key={preset.value} value={preset.value} title={preset.description}>
                    {preset.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
          <Button
            variant="contained"
            startIcon={reportLoading ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
            onClick={handleRunReport}
            disabled={reportLoading}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            {reportLoading ? 'Running...' : `Run ${reportLabel}`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadReport}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'large'}
          >
            Download Excel
          </Button>
        </Box>
      </Paper>

      {/* ===================== SECTION: Report Results ===================== */}
      {(reportLoading || reportData) && (
        <Paper sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {reportData?.metadata?.reportType || currentReport?.label}
              </Typography>
              {reportData && (
                <Chip label={`${reportData.metadata?.rowCount || reportData.rows?.length || 0} rows`} size="small" color="primary" />
              )}
              {reportData?.metadata?.hiddenColumns?.length > 0 && (
                <Tooltip title={`Hidden: ${reportData.metadata.hiddenColumns.join(', ')} — needs the View pay rates permission`}>
                  <Chip size="small" variant="outlined" label={`${reportData.metadata.hiddenColumns.length} hidden`} />
                </Tooltip>
              )}
            </Box>
            {reportData && (
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadReport}>
                Download Excel
              </Button>
            )}
          </Box>

          {/* Loading skeleton */}
          {reportLoading && (
            <Box>
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 0.5 }} />
              ))}
            </Box>
          )}

          {/* Results table */}
          {reportData && reportData.rows && (
            <>
              {/* Custom reports: use ReportBuilderGrid with full formatting */}
              {isCustomReport && selectedCustomReport ? (() => {
                const config = selectedCustomReport.config || {};
                const ds = selectedCustomReport.data_source || 'time_entries';
                const defs = COLUMN_DEFS[ds] || [];

                // Transform label-keyed rows to key-keyed rows
                const keyedRows = reportData.rows.map(row => {
                  const rawRow = {};
                  (reportData.columns || []).forEach(colLabel => {
                    const colDef = defs.find(c => c.label === colLabel);
                    if (colDef) {
                      rawRow[colDef.key] = row[colLabel];
                    } else {
                      rawRow[colLabel] = row[colLabel];
                    }
                  });
                  return rawRow;
                });

                // Build columns prop from config
                const configCols = config.columns || [];
                const calcCols = config.calculatedColumns || {};
                const gridColumns = [
                  ...defs.filter(c => configCols.includes(c.key)),
                  ...Object.entries(calcCols).map(([key, calc]) => ({
                    key,
                    label: calc.headerName || key,
                  })),
                ];

                const gridColumnOrder = (config.columnOrder || configCols)
                  .filter(c => configCols.includes(c) || calcCols[c]);

                return reportData.rows.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No data found for the selected filters</Typography>
                ) : (
                  <Box sx={{ height: 500 }}>
                    <ReportBuilderGrid
                      rowData={keyedRows}
                      columns={gridColumns}
                      columnSettings={config.columnSettings || {}}
                      columnOrder={gridColumnOrder}
                      calculatedColumns={calcCols}
                      conditionalFormatting={config.conditionalFormatting || []}
                      groupConfig={config.groupConfig?.groupBy ? config.groupConfig : undefined}
                      showTotalsRow={config.showTotalsRow || false}
                      onColumnMoved={() => {}}
                      onColumnResized={() => {}}
                      onColumnSelected={() => {}}
                    />
                  </Box>
                );
              })() : (
                /* Built-in reports: original MUI table */
                <>
                  {/* Totals bar */}
                  {reportData.totals && Object.keys(reportData.totals).length > 0 && (
                    <Box sx={{ p: 1.5, mb: 2, bgcolor: 'warning.soft', borderRadius: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {Object.entries(reportData.totals).map(([key, value]) => {
                        const moneyKeys = ['totalPay', 'totalPaid', 'totalRemaining', 'totalAmount', 'grossPay', 'paidAmount', 'remaining'];
                        const isMoney = moneyKeys.includes(key);
                        const num = Number(value);
                        let displayValue;
                        if (String(value).startsWith('$')) {
                          displayValue = value;
                        } else if (!isNaN(num) && value !== '' && value !== null) {
                          displayValue = isMoney
                            ? `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : num % 1 === 0 ? num.toLocaleString() : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        } else {
                          displayValue = value;
                        }
                        return (
                          <Box key={key}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {displayValue}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {reportData.rows.length === 0 ? (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No data found for the selected filters</Typography>
                  ) : isMobile ? (
                    /* Mobile card view */
                    <Stack spacing={1}>
                      {reportData.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                        <Card key={idx} variant="outlined">
                          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                            {reportData.columns.map((col) => (
                              <Box key={col} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                                <Typography variant="caption" color="text.secondary">{col}</Typography>
                                <Typography variant="body2">{row[col] || ''}</Typography>
                              </Box>
                            ))}
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    /* Desktop table view */
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {reportData.columns.map((col) => (
                              <TableCell key={col} sx={{ fontWeight: 'bold', bgcolor: 'background.paper', whiteSpace: 'nowrap' }}>{col}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {reportData.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                            <TableRow key={idx} hover>
                              {reportData.columns.map((col) => (
                                <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>{row[col] || ''}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Pagination */}
                  {reportData.rows.length > 25 && (
                    <TablePagination
                      component="div"
                      count={reportData.rows.length}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      rowsPerPageOptions={[25, 50, 100]}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Custom Report Create/Edit Dialog */}
      <Dialog open={customDialogOpen} onClose={() => setCustomDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {customEditMode ? 'Edit Custom Report' : 'Create Custom Report'}
          <IconButton size="small" onClick={() => setCustomDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Report Name" value={customForm.name} required
                onChange={(e) => setCustomForm(p => ({ ...p, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description (Optional)" value={customForm.description}
                onChange={(e) => setCustomForm(p => ({ ...p, description: e.target.value }))} multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Data Source" value={customForm.data_source}
                onChange={(e) => setCustomForm(p => ({ ...p, data_source: e.target.value, columns: [], sort_column: '' }))}>
                <MenuItem value="time_entries">Time Entries</MenuItem>
                <MenuItem value="expenses">Expenses</MenuItem>
                <MenuItem value="invoices">Invoices</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Columns</Typography>
              <FormGroup row>
                {(COLUMN_OPTIONS[customForm.data_source] || []).map(col => (
                  <FormControlLabel key={col.value} control={
                    <Checkbox size="small" checked={customForm.columns.includes(col.value)}
                      onChange={(e) => {
                        setCustomForm(p => ({
                          ...p, columns: e.target.checked
                            ? [...p.columns, col.value]
                            : p.columns.filter(c => c !== col.value)
                        }));
                      }} />
                  } label={<Typography variant="body2">{col.label}</Typography>} />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Runtime Filters (shown when running)</Typography>
              <FormGroup row>
                {['dateRange', 'employee', 'company', 'department'].map(f => (
                  <FormControlLabel key={f} control={
                    <Checkbox size="small" checked={(customForm.filters || []).includes(f)}
                      onChange={(e) => {
                        setCustomForm(p => ({
                          ...p, filters: e.target.checked
                            ? [...(p.filters || []), f]
                            : (p.filters || []).filter(x => x !== f)
                        }));
                      }} />
                  } label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{f === 'dateRange' ? 'Date Range' : f}</Typography>} />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Group By" value={customForm.grouping}
                onChange={(e) => setCustomForm(p => ({ ...p, grouping: e.target.value }))} size="small">
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="employee">By Employee</MenuItem>
                <MenuItem value="company">By Client</MenuItem>
                {customForm.data_source === 'time_entries' && <MenuItem value="department">By Department</MenuItem>}
              </TextField>
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField select fullWidth label="Sort By" value={customForm.sort_column} size="small"
                onChange={(e) => setCustomForm(p => ({ ...p, sort_column: e.target.value }))}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}>
                <MenuItem value="">Default</MenuItem>
                {(COLUMN_OPTIONS[customForm.data_source] || [])
                  .filter(c => customForm.columns.includes(c.value))
                  .map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4} sm={2}>
              <ToggleButtonGroup value={customForm.sort_order} exclusive size="small" fullWidth
                onChange={(e, val) => val && setCustomForm(p => ({ ...p, sort_order: val }))}>
                <ToggleButton value="ASC">Asc</ToggleButton>
                <ToggleButton value="DESC">Desc</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCustomReport}>
            {customEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess('')} message={success} />

      <ConfirmDialogComponent />
    </Box>
  );
}
